import React from "react";
import { useSelector } from "react-redux";
import { userTypeSelector, authUidSelector } from "store/userSliceHelpers";
import { Navigate } from "react-router-dom";
import { useAuth } from "auth";
import SessionChangedScreen from "components/session-changed/SessionChangedScreen";
import { identityState, isAuthorized } from "./access";

interface Props {
  allowedUserTypes: string[];
  children: React.ReactNode;
}

function ProtectedRoute({ allowedUserTypes, children }: Props) {
  const { user, initializing } = useAuth();
  const userType = useSelector(userTypeSelector);
  const authUid = useSelector(authUidSelector);

  if (initializing) return null;

  const identity = identityState(user, authUid);

  // A Firebase user this tab's stored identity does not belong to: another tab
  // signed someone else in and replaced the shared credentials. Rendering the
  // children would show THIS tab's merchant while every request authenticates as
  // the other one — the CRYP-4918 symptom. Not a redirect to "/", because the
  // login form is itself a way back into the same problem. The screen offers a
  // sign-out instead — the one exit that resolves this without handing the
  // browser to a second principal (CRYP-4925).
  if (identity === "mismatched") return <SessionChangedScreen />;

  if (identity === "signed-out" || !isAuthorized(userType, allowedUserTypes)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
